import { useEffect, useRef, useState } from 'react'
import { Globe } from 'lucide-react'
import { LANGUAGES, useLanguage } from '../context/LanguageContext'
import { cn } from './ui/utils'

interface LanguageMenuProps {
  variant?: 'desktop' | 'mobile'
}

export function LanguageMenu({ variant = 'desktop' }: LanguageMenuProps) {
  const { language, setLanguage } = useLanguage()
  const [open, setOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const menuRef = useRef<HTMLDivElement | null>(null)
  const currentLanguage = LANGUAGES.find((lang) => lang.code === language)

  useEffect(() => {
    if (variant === 'mobile') return undefined
    const handlePointerDown = (event: MouseEvent) => {
      if (
        triggerRef.current?.contains(event.target as Node) ||
        menuRef.current?.contains(event.target as Node)
      ) {
        return
      }
      setOpen(false)
    }
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false)
      }
    }
    document.addEventListener('pointerdown', handlePointerDown)
    document.addEventListener('keydown', handleKey)
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown)
      document.removeEventListener('keydown', handleKey)
    }
  }, [variant])

  if (variant === 'mobile') {
    return (
      <label className="flex flex-col gap-1 text-sm font-medium w-full" data-i18n-ignore="true">
        <span className="text-foreground/70">Language</span>
        <select
          value={language}
          onChange={(event) => setLanguage(event.target.value as typeof language)}
          className="rounded-xl border border-border bg-white px-3 py-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
        >
          {LANGUAGES.map((lang) => (
            <option key={lang.code} value={lang.code}>
              {lang.label}
            </option>
          ))}
        </select>
      </label>
    )
  }

  return (
    <div className="relative" data-i18n-ignore="true">
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
        className={cn(
          'flex items-center gap-2 rounded-full border border-border bg-white/90 px-3 py-1 text-xs font-semibold tracking-wide transition hover:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30',
        )}
      >
        <Globe className="h-4 w-4 text-foreground" aria-hidden />
        <span className="text-foreground">{currentLanguage?.label ?? language.toUpperCase()}</span>
      </button>
      {open && (
        <div
          ref={menuRef}
          role="menu"
          data-i18n-ignore="true"
          className="absolute right-0 z-40 mt-2 w-48 rounded-xl border border-border bg-white p-1 shadow-xl"
        >
          {LANGUAGES.map((lang) => {
            const active = lang.code === language
            return (
              <button
                key={lang.code}
                type="button"
                role="menuitem"
                onClick={() => {
                  setLanguage(lang.code as typeof language)
                  setOpen(false)
                }}
                className={cn(
                  'flex w-full items-center justify-between rounded-lg px-3 py-2 text-sm transition',
                  active
                    ? 'bg-primary/10 text-primary font-semibold'
                    : 'text-foreground hover:bg-muted/60',
                )}
              >
                <span>{lang.label}</span>
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}
